import { useNavigate } from "@tanstack/react-router";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import CheckCircle from "~icons/material-symbols/check-circle";
import Info from "~icons/material-symbols/info-outline";
import { css } from "../../../../styled-system/css";
import type { OsakiChargeBonusCampaignTerm } from "../../../config";
import { t } from "../../../libs/i18n";
import {
  type OsakiChargeBonusCampaignBannerType,
  getOsakiChargeBonusAdditionalSaleNotice,
  getOsakiChargeBonusApplicationDeadlineText,
  getOsakiChargeBonusCampaignTitle,
  getOsakiChargeBonusLotteryResultAnnouncementDateShortText,
} from "../../charge/osaki-charge-bonus-campaign";

type OsakiChargeBonusCampaignBannerProps = {
  bannerType: OsakiChargeBonusCampaignBannerType;
  campaignTerm: OsakiChargeBonusCampaignTerm;
  canApply: boolean;
  appliedQuantity?: number;
  onClickPrepare: () => void;
};

export function getOsakiChargeBonusAppliedQuantityText(
  appliedQuantity: number | null | undefined,
) {
  if (appliedQuantity === 1 || appliedQuantity === 2) {
    return t("{count}口で応募済み", { count: appliedQuantity });
  }

  return t("応募済み");
}

export function OsakiChargeBonusCampaignBanner({
  bannerType,
  campaignTerm,
  canApply,
  appliedQuantity,
  onClickPrepare,
}: OsakiChargeBonusCampaignBannerProps) {
  const navigate = useNavigate();
  const campaignTitle = getOsakiChargeBonusCampaignTitle(campaignTerm);
  const applicationDeadlineText =
    getOsakiChargeBonusApplicationDeadlineText(campaignTerm);
  const lotteryResultAnnouncementDateText =
    getOsakiChargeBonusLotteryResultAnnouncementDateShortText(campaignTerm);
  const appliedQuantityText =
    getOsakiChargeBonusAppliedQuantityText(appliedQuantity);

  if (bannerType === "hidden") {
    return null;
  }

  if (bannerType === "winner") {
    return (
      <section
        className={css({
          bg: "surface.accentSuccessLight",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          p: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "12px",
        })}
      >
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            w: "full",
          })}
        >
          <div
            className={css({
              display: "flex",
              alignItems: "flex-start",
              gap: "4px",
            })}
          >
            <CheckCircle
              className={css({
                color: "text.accentSuccess",
                flexShrink: 0,
                w: "24px",
                h: "24px",
              })}
            />
            <p
              className={css({
                flex: 1,
                minW: 0,
                color: "text.primary",
                fontSize: "16px",
                lineHeight: "1.5",
              })}
            >
              {t("【当選のお知らせ】{title}", { title: campaignTitle })}
            </p>
          </div>
          <p
            className={css({
              color: "text.secondary",
              fontSize: "14px",
              lineHeight: "1.4",
            })}
          >
            {t(
              "当選おめでとうございます！下記の「チャージ手段選択」ボタンからお支払い方法を選択してください。\nクレジット払いはカード番号を入力、コンビニ払いはレジでバーコードを提示して現金でお支払いするとチャージが完了します。",
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: "/charge" })}
          className={css({
            bg: "surface.primary",
            color: "text.primary",
            rounded: "8px",
            minH: "34px",
            px: "12px",
            py: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "1.4",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
          })}
        >
          {t("チャージ手段選択")}
          <ArrowForward fontSize={20} />
        </button>
      </section>
    );
  }

  if (bannerType === "loser") {
    return (
      <section
        className={css({
          bg: "#dbeafe",
          border: "1px solid",
          borderColor: "border.secondary",
          rounded: "12px",
          p: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        })}
      >
        <p
          className={css({
            color: "text.primary",
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "1.5",
          })}
        >
          {t("【落選のお知らせ】{title}", { title: campaignTitle })}
        </p>
        <p
          className={css({
            color: "text.secondary",
            fontSize: "14px",
            lineHeight: "1.4",
          })}
        >
          {t(
            "この度はおおさきポイントアップ商品券事業にご応募いただき、誠にありがとうございました。厳正なる抽選の結果、今回はご期待に添いかねる結果となりました。{notice}",
            { notice: getOsakiChargeBonusAdditionalSaleNotice(campaignTerm) },
          )}
        </p>
      </section>
    );
  }

  const isApplied = bannerType === "applied";

  return (
    <section
      className={css({
        bg: "surface.accentPrimaryLight",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "12px",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      })}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        })}
      >
        <div
          className={css({
            display: "flex",
            alignItems: "flex-start",
            gap: "4px",
          })}
        >
          <Info
            className={css({
              color: "text.accentPrimary",
              flexShrink: 0,
              w: "24px",
              h: "24px",
            })}
          />
          <p
            className={css({
              flex: 1,
              minW: 0,
              color: "text.primary",
              fontSize: "16px",
              lineHeight: "1.5",
            })}
          >
            {campaignTitle}
          </p>
        </div>
        <p
          className={css({
            color: "text.secondary",
            fontSize: "14px",
            lineHeight: "1.4",
          })}
        >
          {t(
            "大崎市にお住まいの方に、大崎市内の店舗限定で利用可能な４割増のみやぎポイントを発行します。1人につき最大２口までお申込みできます。",
          )}
        </p>
      </div>
      {/* 準備するボタン（マイナンバーカードの住所が最新でない場合に活性） */}
      <button
        type="button"
        disabled={isApplied || canApply}
        onClick={onClickPrepare}
        className={css({
          bg: isApplied || canApply ? "surface.disable" : "surface.accentWarn",
          color: isApplied || canApply ? "text.disable" : "white",
          rounded: "2xl",
          px: "14px",
          py: "12px",
          fontWeight: 500,
        })}
      >
        {t("準備する")}
      </button>
      {/* 応募するボタン（マイナンバーカードの住所が最新の場合に活性） */}
      <button
        type="button"
        disabled={isApplied || !canApply}
        onClick={() =>
          navigate({
            to: "/about-campaign",
            search: { type: "osaki-charge-bonus" },
          })
        }
        className={css({
          bg:
            !isApplied && canApply
              ? "surface.accentPrimary"
              : "surface.disable",
          color: !isApplied && canApply ? "white" : "text.disable",
          rounded: "2xl",
          px: "14px",
          py: "12px",
          fontWeight: 500,
        })}
      >
        {isApplied ? appliedQuantityText : t("応募する")}
      </button>
      {!isApplied && (
        <ApplicationGuide
          applicationDeadlineText={applicationDeadlineText}
          lotteryResultAnnouncementDateText={lotteryResultAnnouncementDateText}
        />
      )}
      {isApplied && (
        <p
          className={css({
            color: "text.secondary",
            fontSize: "14px",
            lineHeight: "1.4",
          })}
        >
          {/* 応募状況は文ごと出し分ける。述語つきの句を {status} で差し込むと、日本語以外は
              文の途中に別の文が挟まる形になり、訳しようがなくなる */}
          {appliedQuantity === 1 || appliedQuantity === 2
            ? t(
                "キャンペーンに{count}口で応募済みです。抽選結果の発表日までお待ちください。",
                { count: appliedQuantity },
              )
            : t(
                "キャンペーンに応募済みです。抽選結果の発表日までお待ちください。",
              )}
        </p>
      )}
    </section>
  );
}

type ApplicationGuideProps = {
  applicationDeadlineText: string;
  lotteryResultAnnouncementDateText: string;
};

const ApplicationGuide = ({
  applicationDeadlineText,
  lotteryResultAnnouncementDateText,
}: ApplicationGuideProps) => (
  <div
    className={css({
      bg: "surface.primary",
      rounded: "lg",
      p: "12px",
      display: "flex",
      flexDir: "column",
      gap: "8px",
      fontSize: "14px",
      lineHeight: "1.4",
    })}
  >
    <p className={css({ fontWeight: 600, color: "text.primary" })}>
      {t("応募手順")}
    </p>
    <p className={css({ color: "text.secondary", whiteSpace: "pre-wrap" })}>
      {t(
        "① 登録日が１日より前など住所の再確認が必要な方は「準備する」を押し、マイナンバーカードを読み込みます。\n②「応募する」を押し、希望する購入口数（最大２口）を入力して応募します。\n③ 抽選で当選された方のみ、購入期間中にチャージできます。抽選結果は{date}にお知らせします。",
        { date: lotteryResultAnnouncementDateText },
      )}
    </p>
    <div
      className={css({
        color: "text.secondary",
        display: "flex",
        flexDir: "column",
        gap: "4px",
      })}
    >
      <p className={css({ color: "text.primary", fontWeight: 600 })}>
        {t("■応募期限")}
      </p>
      <p>{applicationDeadlineText}</p>
    </div>
    <div
      className={css({
        color: "text.secondary",
        display: "flex",
        flexDir: "column",
        gap: "4px",
      })}
    >
      <p className={css({ color: "text.primary", fontWeight: 600 })}>
        {t("ご注意事項")}
      </p>
      <p>
        {t(
          "ポイント付与実績を管理するため、応募いただいた方の住所、氏名、生年月日、性別を宮城県から大崎市に提供する場合があります。ご了承の上で応募いただくようお願いします。",
        )}
      </p>
    </div>
  </div>
);
